/**
 * Phone input screen — first step of auth flow.
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  KeyboardAvoidingView,
  ScrollView,
  Platform,
  Alert,
  Image,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { Ionicons } from '@expo/vector-icons';
import { useAuthStore } from '../../src/stores/authStore';
import { useTheme, type ThemeColors } from '../../src/utils/theme';
import { FONT_SIZE, SPACING, RADIUS } from '../../src/utils/design-tokens';
import CountryPicker from '../../src/components/CountryPicker';
import { COUNTRIES, type Country, buildE164Phone, parsePhoneDraft } from '../../src/utils/countries';

const DEFAULT_COUNTRY = COUNTRIES.find((c) => c.iso2 === 'LB')!;
const IS_WEB = Platform.OS === 'web';

export default function PhoneScreen() {
  const router = useRouter();
  const { t } = useTranslation();
  const [country, setCountry] = useState<Country>(DEFAULT_COUNTRY);
  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const requestOtp = useAuthStore((s) => s.requestOtp);
  const { colors } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);

  const syncPhoneDraft = useCallback((value: string) => {
    const parsed = parsePhoneDraft(value, country);
    if (parsed.country.iso2 !== country.iso2) {
      setCountry(parsed.country);
    }
    setPhone(parsed.localNumber);
    if (phoneError) setPhoneError('');
    if (submitError) setSubmitError('');
    return parsed;
  }, [country, phoneError, submitError]);

  const handleContinue = async () => {
    const parsed = parsePhoneDraft(phone, country);
    const localDigits = parsed.localNumber.replace(/[^0-9]/g, '');
    if (localDigits.length < 4) {
      setPhoneError(t('auth.invalidPhone'));
      return;
    }

    setPhone(parsed.localNumber);
    if (parsed.country.iso2 !== country.iso2) {
      setCountry(parsed.country);
    }
    setPhoneError('');
    setSubmitError('');

    const fullNumber = buildE164Phone(parsed.localNumber, parsed.country);

    setIsLoading(true);
    try {
      const result = await requestOtp(fullNumber);
      router.push({
        pathname: '/(auth)/otp',
        params: {
          phone: fullNumber,
          isTestAccount: result.isTestAccount ? '1' : '0',
          inferredTz: parsed.country.timezone,
        },
      });
    } catch (err: any) {
      const msg = err?.response?.data?.error || t('auth.otpFailed');
      if (IS_WEB) {
        setSubmitError(msg);
      } else {
        Alert.alert(t('auth.error'), msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formContent = (
    <View style={IS_WEB ? s.card : s.content}>
      {/* Logo */}
      <View style={s.logoContainer}>
        <Image source={require('../../assets/logo.png')} style={s.logoImage} />
        <Text style={s.appName}>FishMemory</Text>
        <Text style={s.tagline}>{t('auth.tagline')}</Text>
      </View>

      {/* Phone input */}
      <View style={s.inputSection}>
        <Text style={s.label}>{t('auth.phoneLabel')}</Text>
        <View style={s.phoneRow}>
          <CountryPicker selectedCountry={country} onSelect={setCountry} />
          <TextInput
            style={[s.input, s.phoneInput, phoneError ? { borderColor: colors.error } : null]}
            value={phone}
            onChangeText={syncPhoneDraft}
            onBlur={() => {
              const parsed = parsePhoneDraft(phone, country);
              if (parsed.country.iso2 !== country.iso2) {
                setCountry(parsed.country);
              }
              setPhone(parsed.localNumber);
              const d = parsed.localNumber.replace(/[^0-9]/g, '');
              if (d.length > 0 && d.length < 4) setPhoneError(t('auth.invalidPhone'));
            }}
            keyboardType="phone-pad"
            autoFocus={!IS_WEB}
            placeholder={t('auth.phonePlaceholder')}
            placeholderTextColor={colors.textSecondary}
            textContentType="telephoneNumber"
            accessibilityLabel={t('auth.phoneLabel')}
          />
        </View>
        {phoneError ? <Text style={s.errorText}>{phoneError}</Text> : null}
        {submitError ? (
          <View style={s.submitErrorBox}>
            <Ionicons name="alert-circle-outline" size={15} color={colors.error} />
            <Text style={s.errorText}>{submitError}</Text>
          </View>
        ) : null}
        <Text style={s.hint}>{t('auth.phoneHint')}</Text>
      </View>

      {/* Auth progress stepper */}
      <View style={s.stepper}>
        <View style={[s.stepDot, s.stepActive]} />
        <View style={s.stepLine} />
        <View style={s.stepDot} />
        <View style={s.stepLine} />
        <View style={s.stepDot} />
      </View>

      {/* Continue button */}
      <TouchableOpacity
        onPress={handleContinue}
        disabled={isLoading}
        accessibilityRole="button"
        accessibilityLabel={t('auth.continue')}
      >
        <LinearGradient
          colors={isLoading ? [colors.textSecondary, colors.textSecondary] : ['#64B5F6', '#1E88E5']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={s.button}
        >
          {isLoading ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <View style={s.buttonContent}>
              <Text style={s.buttonText}>{t('auth.continue')}</Text>
              <Ionicons name="arrow-forward" size={18} color="#fff" />
            </View>
          )}
        </LinearGradient>
      </TouchableOpacity>

      {/* Terms footer — web only */}
      {IS_WEB && (
        <Text style={s.terms}>
          By continuing you agree to our Terms of Service and Privacy Policy
        </Text>
      )}
    </View>
  );

  // Web: ScrollView with centered card, no KeyboardAvoidingView (irrelevant on web)
  if (IS_WEB) {
    return (
      <ScrollView
        style={s.container}
        contentContainerStyle={s.webScrollContent}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        {formContent}
      </ScrollView>
    );
  }

  // Native: KeyboardAvoidingView with proper behavior per platform
  return (
    <KeyboardAvoidingView
      style={s.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <ScrollView
        contentContainerStyle={s.nativeScrollContent}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        {formContent}
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: IS_WEB ? colors.background : colors.background,
  },
  // Web: full-page scroll container, content sits upper-center
  webScrollContent: {
    flexGrow: 1,
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingTop: 48,
    paddingBottom: 40,
    paddingHorizontal: 16,
    backgroundColor: colors.background,
  },
  // Native: scroll fills available space
  nativeScrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
  },
  // Web card: contained box with max-width, overflow hidden to clip children
  card: {
    width: '100%',
    maxWidth: 440,
    backgroundColor: colors.card,
    borderRadius: RADIUS.lg,
    padding: 36,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.08,
    shadowRadius: 20,
    elevation: 4,
  },
  // Native content: fills screen with padding
  content: {
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: 32,
    paddingVertical: 32,
  },
  logoContainer: {
    alignItems: 'center',
    marginBottom: IS_WEB ? 28 : 40,
  },
  logoImage: {
    width: IS_WEB ? 72 : 96,
    height: IS_WEB ? 72 : 96,
    marginBottom: 10,
    resizeMode: 'contain',
  },
  appName: {
    fontSize: FONT_SIZE['2xl'],
    fontFamily: 'PlusJakartaSans_700Bold',
    color: colors.text,
    marginBottom: SPACING.xs,
  },
  tagline: {
    fontSize: FONT_SIZE.sm,
    fontFamily: 'Inter_400Regular',
    color: colors.textSecondary,
    textAlign: 'center',
    maxWidth: 260,
  },
  inputSection: {
    marginBottom: SPACING.xl,
  },
  label: {
    fontSize: FONT_SIZE.sm,
    fontFamily: 'Inter_600SemiBold',
    color: colors.text,
    marginBottom: SPACING.sm,
  },
  phoneRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 8,
    // Prevent row from overflowing card on web
    ...(IS_WEB ? { overflow: 'hidden' as any } : {}),
  },
  input: {
    backgroundColor: IS_WEB ? colors.background : colors.card,
    borderRadius: RADIUS.md,
    paddingHorizontal: SPACING.base,
    paddingVertical: SPACING.md,
    fontSize: FONT_SIZE.md,
    fontFamily: 'Inter_400Regular',
    color: colors.text,
    borderWidth: 1,
    borderColor: colors.border,
  },
  phoneInput: {
    flex: 1,
    width: 0,
    minWidth: 0,
    fontSize: FONT_SIZE.base,
    letterSpacing: 0.2,
  },
  hint: {
    fontSize: FONT_SIZE.xs,
    fontFamily: 'Inter_400Regular',
    color: colors.textSecondary,
    marginTop: SPACING.sm,
  },
  submitErrorBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: SPACING.xs,
  },
  stepper: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  stepDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: colors.border,
  },
  stepActive: {
    backgroundColor: colors.primary,
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  stepLine: {
    width: 28,
    height: 2,
    backgroundColor: colors.border,
    marginHorizontal: 4,
  },
  button: {
    borderRadius: RADIUS.md,
    paddingVertical: SPACING.base,
    alignItems: 'center',
  },
  buttonText: {
    color: '#fff',
    fontSize: FONT_SIZE.base,
    fontFamily: 'Inter_600SemiBold',
  },
  buttonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
  },
  errorText: {
    color: colors.error,
    fontSize: FONT_SIZE.sm,
    fontFamily: 'Inter_400Regular',
    marginTop: SPACING.xs,
  },
  terms: {
    marginTop: SPACING.xl,
    fontSize: 11,
    fontFamily: 'Inter_400Regular',
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: 17,
  },
});
